import React from 'react';
import { Alert, Box, Button, Paper, Typography } from '@mui/material';
import {
  buildSafetyCaseControlsPath,
  controlRecordedDescription,
  pickSharedWorkflowControl,
  resolveInvestigateSafetyCaseId,
  type InvestigateControlActionCollection,
} from '../../domain/safetyCases/investigateControlLink';
import { PATTERN_MANAGED_IN_CASE_LABEL, PATTERN_OPEN_SAFETY_CASE_ACTION } from '../../domain/incidents/patternOwnershipContainment';
import { buildLiveSafetyCasePath } from '../../utils/acceptanceHierarchyNavigation';

type Props = {
  safetyCaseId?: string | null;
  orgId?: string | null;
  siteId?: string | null;
  workflowId?: string | null;
  incidentId?: string | null;
  incident?: {
    safety_case_id?: string | null;
    finding_id?: string | null;
    safety_case?: { safety_case_id?: string | null; finding_id?: string | null } | null;
  } | null;
  workflowCorrectiveActions?: InvestigateControlActionCollection | null;
  navigate: (to: string) => void;
};

const InvestigateControlLinkCard: React.FC<Props> = ({
  safetyCaseId,
  orgId,
  siteId,
  workflowId,
  incidentId,
  incident,
  workflowCorrectiveActions,
  navigate,
}) => {
  const caseId = resolveInvestigateSafetyCaseId({
    contextSafetyCaseId: safetyCaseId,
    incident,
  });
  const recorded = pickSharedWorkflowControl(workflowCorrectiveActions);
  const hasControl = Boolean(recorded);
  const description = controlRecordedDescription(recorded);
  const href = caseId
    ? buildSafetyCaseControlsPath({
      safetyCaseId: caseId,
      orgId,
      siteId,
      workflowId,
      incidentId,
    })
    : '';
  const caseHref = caseId
    ? buildLiveSafetyCasePath({
      caseId,
      orgId,
      siteId,
      workflowId,
      incidentId,
    })
    : '';

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, mb: 1.25, borderRadius: 2.5 }}
      data-testid="investigate-control-link"
    >
      <Typography
        sx={{ fontSize: 10.5, letterSpacing: 0.95, color: 'text.secondary', textTransform: 'uppercase', fontWeight: 800 }}
        data-testid="investigate-managed-in-safety-case"
      >
        {PATTERN_MANAGED_IN_CASE_LABEL}
      </Typography>
      {hasControl ? (
        <Box sx={{ mt: 0.75 }}>
          <Typography sx={{ fontWeight: 800 }} data-testid="investigate-control-recorded-label">
            Control recorded
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.55 }}>
            {description || 'A site control is on file for this Safety Case.'}
          </Typography>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.55 }}>
          Record what was done on the Safety Case. Investigate stays on evidence and pattern.
        </Typography>
      )}
      {!caseId ? (
        <Alert severity="info" sx={{ mt: 1.25, borderRadius: 2 }}>
          This pattern is not linked to a Safety Case yet, so the control cannot be opened from here.
        </Alert>
      ) : (
        <Box>
          <Button
            variant="contained"
            size="small"
            sx={{ mt: 1.25, fontWeight: 800 }}
            data-testid="investigate-open-safety-case"
            onClick={() => navigate(caseHref)}
          >
            {PATTERN_OPEN_SAFETY_CASE_ACTION}
          </Button>
          <Button
            variant="text"
            size="small"
            sx={{ mt: 0.5, ml: 1, fontWeight: 700, textTransform: 'none' }}
            data-testid={hasControl ? 'investigate-view-control' : 'investigate-record-control'}
            onClick={() => navigate(href)}
          >
            {hasControl ? 'View control' : 'Record control'}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default InvestigateControlLinkCard;
